/*  ReferralRewardsCard — Rewards tile inside Profile → Refer & Earn.
 *
 *  iter100.200 — Doctor-facing summary of the referral rewards ledger.
 *  Companion to <SandboxLinkGenerator>. Shows:
 *    - Pending referrals (admin hasn't reviewed yet)
 *    - Approved referrals + total ₹ credited
 *    - Badges earned via referrals (Invisique Ambassador)
 *    - Recent history rows with visitor + trigger + status
 *
 *  Rewards are set by the admin on approval (b3 = D), so this surface
 *  intentionally shows the doctor exactly what the admin decided —
 *  no reward-amount guessing on the doctor's side.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
    Gift, HourglassMedium, CheckCircle, XCircle, Trophy, Coins, Certificate,
} from "@phosphor-icons/react";


const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function relativeTime(iso) {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "—";
    const diff = Date.now() - t;
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
}


const STATUS_META = {
    pending:  { Icon: HourglassMedium, cls: "bg-amber-50 text-amber-800 ring-amber-200",  label: "Pending" },
    approved: { Icon: CheckCircle,     cls: "bg-emerald-50 text-emerald-800 ring-emerald-200", label: "Approved" },
    rejected: { Icon: XCircle,         cls: "bg-slate-100 text-slate-700 ring-slate-200",  label: "Rejected" },
};

const TRIGGER_LABEL = {
    claim: "Sandbox claim",
    conversion: "Real signup",
};


export default function ReferralRewardsCard() {
    const [data, setData] = useState(null);
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/doctor/rewards/summary").then((r) => r.data).catch(() => null),
            api.get("/doctor/credits/balance").then((r) => r.data).catch(() => null),
        ]).then(([summary, bal]) => {
            setData(summary);
            setCredits(bal);
            setLoading(false);
        });
    }, []);

    const totals = useMemo(() => {
        if (!data) return { pending: 0, approved: 0, rejected: 0, earned: 0, badges: 0 };
        return {
            pending: data.counts?.pending || 0,
            approved: data.counts?.approved || 0,
            rejected: data.counts?.rejected || 0,
            earned: data.total_approved_inr || 0,
            badges: (data.badges_earned || []).length,
        };
    }, [data]);

    // iter100.202 — Credit-ledger aware. `available` is the live wallet
    // the doctor can actually spend; `earned` is the lifetime approved
    // reward sum from the referrals ledger (which decouples from
    // available balance the moment credit gets applied to an invoice).
    const availableCredit = credits?.available_inr || 0;
    const spentCredit = credits?.total_spent_inr || 0;

    if (loading) {
        return (
            <div
                className="rounded-3xl border border-border bg-card p-5 text-[12px] text-muted-foreground"
                data-testid="referral-rewards-card-loading"
            >
                Loading your referral rewards…
            </div>
        );
    }

    const isEmpty = data && totals.pending === 0 && totals.approved === 0 && totals.rejected === 0;

    return (
        <div
            className="rounded-3xl border border-border bg-card overflow-hidden mb-5"
            data-testid="referral-rewards-card"
        >
            <div className="px-5 py-4 border-b border-border bg-gradient-to-r from-emerald-50/60 to-transparent flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                    <Gift size={20} weight="duotone" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="font-display text-base font-bold leading-tight">Your referral rewards</h3>
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                        Credit is issued once an admin approves each referral.
                        {isEmpty ? " Nothing to show yet — share a link below to get started." : ""}
                    </p>
                </div>
            </div>

            {/* KPI strip — 5 tiles now (was 4). Available credit is the
                new hero — that's the live wallet the doctor sees applied
                on their next invoice. */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-border/70 border-b border-border">
                <KpiCell
                    label="Available"
                    value={fmtINR(availableCredit)}
                    sub={availableCredit > 0 ? "applies to next invoice" : "empty wallet"}
                    icon={<Coins size={13} weight="fill" className="text-emerald-600" />}
                    highlight={availableCredit > 0}
                    testid="referral-available-credit"
                />
                <KpiCell
                    label="Lifetime earned"
                    value={fmtINR(totals.earned)}
                    sub={spentCredit > 0 ? `${fmtINR(spentCredit)} used` : (totals.approved > 0 ? `${totals.approved} approved` : "none yet")}
                    icon={<Trophy size={13} weight="fill" className="text-primary" />}
                    testid="referral-total-earned"
                />
                <KpiCell
                    label="Pending"
                    value={totals.pending}
                    sub={totals.pending > 0 ? "admin reviewing" : "no queue"}
                    icon={<HourglassMedium size={13} weight="duotone" className="text-amber-600" />}
                    testid="referral-pending-count"
                />
                <KpiCell
                    label="Approved"
                    value={totals.approved}
                    sub={totals.approved > 0 ? "credited" : "none yet"}
                    icon={<CheckCircle size={13} weight="fill" className="text-primary" />}
                    testid="referral-approved-count"
                />
                <KpiCell
                    label="Badges"
                    value={totals.badges}
                    sub={totals.badges > 0 ? "on microsite" : "earn one"}
                    icon={<Trophy size={13} weight="fill" className="text-violet-600" />}
                    testid="referral-badges-count"
                />
            </div>

            {/* Badges earned strip */}
            {(data?.badges_earned || []).length > 0 && (
                <div className="px-5 py-3 bg-violet-50/40 border-b border-border" data-testid="referral-badges-strip">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-violet-800">
                            Referral badges earned
                        </span>
                        {(data.badges_earned || []).map((b) => (
                            <span
                                key={b.key}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white ring-1 ring-violet-200 text-[11px] font-semibold text-violet-800"
                                title={b.granted_at ? `Granted ${new Date(b.granted_at).toLocaleDateString()}` : ""}
                            >
                                <Certificate size={11} weight="fill" />
                                {b.key.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* History list */}
            {(data?.history || []).length > 0 && (
                <div data-testid="referral-history-list">
                    <div className="px-5 py-2.5 border-b border-border text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                        Recent activity
                    </div>
                    <ul className="divide-y divide-border">
                        {data.history.slice(0, 10).map((r) => {
                            const meta = STATUS_META[r.status] || STATUS_META.pending;
                            const StatusIcon = meta.Icon;
                            return (
                                <li
                                    key={r.id}
                                    className="px-5 py-3 flex items-start gap-3"
                                    data-testid={`referral-history-row-${r.id}`}
                                >
                                    <span
                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${meta.cls} shrink-0 mt-0.5`}
                                    >
                                        <StatusIcon size={10} weight="fill" />
                                        {meta.label}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-medium truncate">
                                            {r.visitor_name || "Sandbox visitor"}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground truncate">
                                            {TRIGGER_LABEL[r.triggered_by] || r.triggered_by}
                                            {r.visitor_email ? <> · {r.visitor_email}</> : null}
                                        </div>
                                        {r.status === "approved" && (r.reward_amount_inr || r.reward_badge_key) && (
                                            <div className="text-[11px] text-emerald-800 font-semibold mt-0.5">
                                                Reward:
                                                {r.reward_amount_inr ? ` ${fmtINR(r.reward_amount_inr)}` : ""}
                                                {r.reward_badge_key ? ` · ${r.reward_badge_key.replace(/_/g, " ")}` : ""}
                                                {r.reward_notes ? <span className="font-normal text-emerald-900/70 ml-1">— {r.reward_notes}</span> : null}
                                            </div>
                                        )}
                                        {r.status === "rejected" && r.rejection_reason && (
                                            <div className="text-[11px] text-slate-600 mt-0.5 italic">
                                                &ldquo;{r.rejection_reason}&rdquo;
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0">
                                        {relativeTime(r.created_at)}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}


function KpiCell({ label, value, sub, icon, highlight = false, testid }) {
    return (
        <div className={`px-4 py-3 ${highlight ? "bg-emerald-50/70" : "bg-card"}`} data-testid={testid}>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1.5">
                {icon}
                {label}
            </div>
            <div className={`text-xl font-bold tabular-nums mt-0.5 leading-tight ${highlight ? "text-emerald-700" : "text-foreground"}`}>
                {value}
            </div>
            {sub && (
                <div className="text-[10.5px] text-muted-foreground mt-0.5">{sub}</div>
            )}
        </div>
    );
}
